/**
 * Marketing Site Map
 *
 * Shared route definitions for the public marketing shell.
 *
 * Primary responsibilities:
 * - Keep public marketing routes centralized
 * - Build localized navigation and footer links from route definitions
 *
 * Dependencies:
 * - marketing copy
 */

import { getMarketingCopy } from '@/lib/marketing-copy'

type MarketingNavKey =
  | 'home'
  | 'features'
  | 'docs'
  | 'importAi'
  | 'aiReadiness'
  | 'agents'
  | 'madeInCanada'
  | 'support'
  | 'privacy'
  | 'terms'

interface MarketingNavDefinition {
  href: string
  key: MarketingNavKey
  external?: boolean
}

export interface MarketingNavItem {
  href: string
  label: string
  external?: boolean
}

export interface MarketingFooterGroup {
  title: string
  items: MarketingNavItem[]
}

const primaryNavDefinitions: MarketingNavDefinition[] = [
  { href: '/', key: 'home' },
  { href: '/features', key: 'features' },
  { href: '/import', key: 'importAi' },
  { href: '/support', key: 'support' },
  { href: '/agents', key: 'agents' }
]

const footerDefinitions = {
  platform: [
    { href: '/features', key: 'features' },
    { href: '/import', key: 'importAi' },
    { href: '/ai-readiness', key: 'aiReadiness' },
    { href: '/agents', key: 'agents' },
    { href: '/docs', key: 'docs' }
  ] satisfies MarketingNavDefinition[],
  company: [
    { href: '/made-in-canada', key: 'madeInCanada' },
    { href: '/support', key: 'support' },
    { href: 'https://www.marshmallow.studio', key: 'support', external: true }
  ] satisfies MarketingNavDefinition[],
  legal: [
    { href: '/privacy', key: 'privacy' },
    { href: '/terms', key: 'terms' }
  ] satisfies MarketingNavDefinition[]
}

function mapNavItem(
  definition: MarketingNavDefinition,
  labels: ReturnType<typeof getMarketingCopy>['common'],
  studioLabel: string
): MarketingNavItem {
  if (definition.external) {
    return {
      href: definition.href,
      label: studioLabel,
      external: true
    }
  }

  return {
    href: definition.href,
    label: labels[definition.key]
  }
}

export function buildMarketingPrimaryNavItems(language?: string): MarketingNavItem[] {
  const copy = getMarketingCopy(language)

  return primaryNavDefinitions.map((definition) =>
    mapNavItem(definition, copy.common, copy.footer.studioLabel)
  )
}

export function buildMarketingFooterGroups(language?: string): MarketingFooterGroup[] {
  const copy = getMarketingCopy(language)

  return [
    {
      title: copy.footer.platformTitle,
      items: footerDefinitions.platform.map((definition) =>
        mapNavItem(definition, copy.common, copy.footer.studioLabel)
      )
    },
    {
      title: copy.footer.companyTitle,
      items: footerDefinitions.company.map((definition) =>
        mapNavItem(definition, copy.common, copy.footer.studioLabel)
      )
    },
    {
      title: copy.footer.legalTitle,
      items: footerDefinitions.legal.map((definition) =>
        mapNavItem(definition, copy.common, copy.footer.studioLabel)
      )
    }
  ]
}
